import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** Image source. Falls back to `fallback` (or first letter of `name`) on error or absence. */
  src?: string;
  /** User name; the first character is used as the fallback initial. */
  name?: string;
  /** Explicit fallback content (overrides `name`'s first character). */
  fallback?: string;
  size?: AvatarSize;
  /** alt text for the image, when `src` is provided. */
  alt?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-5 w-5 text-eyebrow',
  md: 'h-[26px] w-[26px] text-2xs',
  lg: 'h-9 w-9 text-body',
};

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { src, name, fallback, size = 'md', alt, className, ...rest },
  ref,
) {
  const [imgFailed, setImgFailed] = useState(false);
  // `||` not `??`: charAt on an empty/whitespace name returns '' (not
  // undefined), which must still fall through to '?'.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const initial = (fallback || name?.trim().charAt(0) || '?').toUpperCase();
  const showImage = src && !imgFailed;

  return (
    <span
      ref={ref}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        // One accent only: solid brand fill + cream initial (was a purple→magenta
        // gradient with hardcoded hex + text-white that broke dark mode).
        'bg-brand font-medium leading-none text-canvas',
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name ?? ''}
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{initial}</span>
      )}
    </span>
  );
});
